import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { X, MessagesSquare } from "lucide-react";
import api from "../../api";

// Ask one of my supervisors about this patient (supervision). From a session
// note it's about that session; from the patient file `sessions`
// ([{consultation_id, date, doctor_name}], newest first) lets the clinician
// pick one, or leave it about the whole case.
const fmtDay = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");

export default function AskSupervisionModal({ patientId, patientName, consultationId, sessions = [], onClose }) {
  const [sups, setSups] = useState(null);
  const [about, setAbout] = useState("");
  const [form, setForm] = useState({ supervisor_id: "", question: "", priority: "routine" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.getMySupervisors().then((r) => { setSups(r.data || []); if ((r.data || []).length === 1) setForm((f) => ({ ...f, supervisor_id: r.data[0].id })); })
      .catch(() => setSups([]));
  }, []);
  const send = async () => {
    setBusy(true);
    try {
      await api.requestSupervision({ ...form, patient_id: patientId, consultation_id: consultationId || (about ? Number(about) : undefined) });
      toast.success("Sent - you'll be told when they reply");
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't send it.");
    } finally {
      setBusy(false);
    }
  };
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => onClose(false)}>
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="flex items-center gap-2 text-base font-bold text-secondary-900"><MessagesSquare className="w-5 h-5 text-primary-600" /> Ask for supervision</h3>
          <button type="button" onClick={() => onClose(false)} aria-label="Close" className="text-secondary-400"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-secondary-600">About {patientName || "this patient"}. Your supervisor sees the patient's record to prepare.</p>
        {sups === null ? <p className="text-sm text-secondary-500">Loading…</p> : sups.length === 0 ? (
          <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">You don't have a supervisor yet. An admin can add one under People → Supervisors.</p>
        ) : (
          <>
            <select value={form.supervisor_id} onChange={(e) => setForm({ ...form, supervisor_id: parseInt(e.target.value, 10) || "" })} aria-label="Supervisor" className="w-full rounded-lg border border-secondary-300 p-2 text-sm bg-white">
              <option value="">Who would you like to ask?</option>
              {sups.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {!consultationId && sessions.length > 0 && (
              <select value={about} onChange={(e) => setAbout(e.target.value)} aria-label="What it's about" className="w-full rounded-lg border border-secondary-300 p-2 text-sm bg-white">
                <option value="">About the whole case</option>
                {sessions.map((x) => <option key={x.consultation_id} value={x.consultation_id}>About the session on {fmtDay(x.date)}{x.doctor_name ? ` (${x.doctor_name})` : ""}</option>)}
              </select>
            )}
            <textarea value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} rows={4} maxLength={3000}
              placeholder="What would you like help with? e.g. The patient keeps avoiding exposure work - how should I approach it?" aria-label="Question"
              className="w-full rounded-lg border border-secondary-300 p-2 text-sm" />
            <div className="flex gap-1.5">
              {[["routine", "Routine"], ["soon", "This week"], ["urgent", "Urgent"]].map(([k, v]) => (
                <button key={k} type="button" onClick={() => setForm({ ...form, priority: k })}
                  className={`rounded-full border px-3 py-1 text-xs font-medium ${form.priority === k ? (k === "urgent" ? "border-rose-500 bg-rose-50 text-rose-800" : "border-primary-500 bg-primary-50 text-primary-800") : "border-secondary-300 text-secondary-700"}`}>{v}</button>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => onClose(false)} className="rounded-lg border border-secondary-300 px-4 py-2 text-sm">Cancel</button>
              <button type="button" onClick={send} disabled={busy || !form.supervisor_id || form.question.trim().length < 10} className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{busy ? "Sending…" : "Send"}</button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
